import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useSite } from '../context/SiteContext';
import { trackContactEvent } from '../utils/trackingEvents';
import ContactForm from './ContactForm';

const FALLBACK = {
  phone: '(43) 3885-3885',
  whatsapp: '(43) 9611-2193',
  email: 'contato@sperandiooralcenter.com.br',
  address: 'R. Rio Branco, 330 Sala-02\nCentro, Apucarana - PR\nCEP: 86800-120',
  addressShort: 'R. Rio Branco, 330 Sala-02',
  addressCity: 'Centro, Apucarana - PR',
  cep: '86800-120',
  businessHours: [
    { day: 'Segunda a Sexta', hours: '9h às 17:30' },
    { day: 'Sábado', hours: '9h às 12:30' },
    { day: 'Domingo', hours: 'Fechado' },
  ],
  facebook: 'https://www.facebook.com/people/Sperandio-Oral-Center/61558475248763/#',
  instagram: 'https://instagram.com/sperandiooralcenter',
  mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.8977!2d-51.4607!3d-23.5505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sR.%20Rio%20Branco%2C%20330%20Sala-02%20-%20Centro%2C%20Apucarana%20-%20PR%2C%2086800-120!5e0!3m2!1spt-BR!2sbr!4v1234567890123!5m2!1spt-BR!2sbr',
};

const Contact: React.FC = () => {
  const { config, getPage, whatsappUrl } = useSite();
  const page = getPage('contact') || getPage('contato');

  const phone = config?.phone || FALLBACK.phone;
  const whatsapp = config?.whatsapp || FALLBACK.whatsapp;
  const email = config?.email || FALLBACK.email;
  const address = config?.address || FALLBACK.address;
  const facebook = config?.facebook_url || FALLBACK.facebook;
  const instagram = config?.instagram_url || FALLBACK.instagram;

  const hours = config?.business_hours?.length
    ? config.business_hours
    : FALLBACK.businessHours;

  const lat = config?.latitude || '';
  const lng = config?.longitude || '';
  const mapSrc = lat && lng
    ? `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s${encodeURIComponent(address)}!5e0!3m2!1spt-BR!2sbr`
    : FALLBACK.mapSrc;

  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;

  return (
    <>
      {/* Hero */}
      <section className="bg-profile-1 text-white py-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h1 className="display-4 fw-bold mb-3">{page?.page_title || 'Entre em Contato'}</h1>
              <p className="lead">{page?.page_description || 'Estamos prontos para atendê-lo e esclarecer todas as suas dúvidas'}</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Info Cards + Form */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="g-4 mb-5">
            {/* Telefone */}
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body className="p-4">
                  <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-phone fs-2 text-profile-3"></i>
                  </div>
                  <h5 className="text-profile-1 fw-bold mb-3">Telefone</h5>
                  <p className="text-muted mb-2">{phone}</p>
                  <p className="text-muted mb-3"><i className="fab fa-whatsapp me-2 text-success"></i>{whatsapp}</p>
                  <a href={whatsappUrl} className="btn bg-profile-1 text-white btn-sm" target="_blank" rel="noopener noreferrer" onClick={trackContactEvent}>
                    <i className="fab fa-whatsapp me-2"></i>WhatsApp
                  </a>
                </Card.Body>
              </Card>
            </Col>

            {/* Email */}
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body className="p-4">
                  <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-envelope fs-2 text-profile-3"></i>
                  </div>
                  <h5 className="text-profile-1 fw-bold mb-3">Email</h5>
                  <p className="text-muted mb-3">{email}</p>
                  <a href={`mailto:${email}`} className="btn bg-profile-1 text-white btn-sm">
                    <i className="fas fa-envelope me-2"></i>Enviar Email
                  </a>
                </Card.Body>
              </Card>
            </Col>

            {/* Horário */}
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body className="p-4">
                  <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-clock fs-2 text-profile-3"></i>
                  </div>
                  <h5 className="text-profile-1 fw-bold mb-3">Horário</h5>
                  <div className="text-muted small">
                    {hours.map((h, i) => (
                      <p className="mb-1" key={i}>{h.day}: {h.hours}</p>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Form */}
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-5">
                  <h3 className="text-profile-1 fw-bold text-center mb-4">Envie sua Mensagem</h3>
                  <ContactForm />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Localização */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 className="text-profile-1 fw-bold mb-3">Nossa Localização</h2>
              <p className="text-muted lead">Estamos estrategicamente localizados no centro de Apucarana para melhor atendê-lo</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col lg={8}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="p-0">
                  <div className="ratio ratio-16x9">
                    <iframe
                      src={mapSrc}
                      width="100%" height="100%"
                      allowFullScreen loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localização da Sperandio Oral Center"
                      style={{ border: 0, borderRadius: '0.375rem' }}
                    ></iframe>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="p-4">
                  <h4 className="text-profile-1 fw-bold mb-4">
                    <i className="fas fa-map-marker-alt me-2 text-profile-3"></i>Endereço
                  </h4>

                  <div className="mb-4">
                    <h6 className="fw-bold text-profile-1 mb-2">Localização</h6>
                    <p className="text-muted mb-0">
                      <i className="fas fa-map-marker-alt me-2 text-profile-3"></i>
                      {address.split('\n').map((line, i) => (
                        <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
                      ))}
                    </p>
                  </div>

                  <div className="mb-4">
                    <h6 className="fw-bold text-profile-1 mb-2">Contato</h6>
                    <p className="text-muted mb-2">
                      <i className="fas fa-phone-alt me-2 text-profile-3"></i>
                      <a href={`tel:${phone.replace(/\D/g, '')}`} className="text-decoration-none text-muted">{phone}</a>
                    </p>
                    <p className="text-muted mb-2">
                      <i className="fab fa-whatsapp me-2 text-profile-3"></i>
                      <a href={whatsappUrl} className="text-decoration-none text-muted" target="_blank" rel="noopener noreferrer">{whatsapp}</a>
                    </p>
                    <p className="text-muted mb-0">
                      <i className="fas fa-envelope me-2 text-profile-3"></i>
                      <a href={`mailto:${email}`} className="text-decoration-none text-muted">{email}</a>
                    </p>
                  </div>

                  <div className="mb-4">
                    <h6 className="fw-bold text-profile-1 mb-2">Horário de Funcionamento</h6>
                    <div className="text-muted small">
                      {hours.map((h, i) => (
                        <p className="mb-1" key={i}><i className="far fa-clock me-2 text-profile-3"></i>{h.day}: {h.hours}</p>
                      ))}
                    </div>
                  </div>

                  <div className="d-grid">
                    <a href={mapsUrl} className="btn bg-profile-1 text-white" target="_blank" rel="noopener noreferrer">
                      <i className="fas fa-directions me-2"></i>Como Chegar
                    </a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Redes Sociais */}
      <section className="py-5 bg-profile-1 text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h3 className="fw-bold mb-4">Siga-nos nas Redes Sociais</h3>
              <p className="mb-4">Acompanhe nossas novidades, dicas de saúde bucal e depoimentos de pacientes</p>
              <div className="d-flex justify-content-center gap-3">
                <a href={facebook} className="btn btn-outline-light btn-lg rounded-circle" target="_blank" rel="noopener noreferrer" style={{ width: '60px', height: '60px' }}>
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href={instagram} className="btn btn-outline-light btn-lg rounded-circle" target="_blank" rel="noopener noreferrer" style={{ width: '60px', height: '60px' }}>
                  <i className="fab fa-instagram"></i>
                </a>
                <a href={whatsappUrl} className="btn btn-outline-light btn-lg rounded-circle" target="_blank" rel="noopener noreferrer" onClick={trackContactEvent} style={{ width: '60px', height: '60px' }}>
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Contact;
